import type { CarePlan, Patient, Reference } from '@medplum/fhirtypes'
import { mapDemographicsQuestionnaireResponses } from '@seen/core/models/care-plans/demographics'
import { InitialCarePlan } from '@seen/core/models/care-plans/InitialCarePlan'
import {
  ExtendedQuestionnaireResponse,
  type QuestionnaireWithResponse,
} from '@seen/core/models/questionnaireResponse'
import * as questionnaires from '@seen/core/models/questionnaires/index'
import { compact } from 'lodash-es'
import { useMemo } from 'react'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { useSearchOneOrCreate } from '#src/hooks/useSearchOneOrCreate.ts'

interface UseCarePlanDemographicsResult {
  demographics: QuestionnaireWithResponse[]
  isLoading: boolean
  error: Error | null
}

export function useCarePlanDemographics({
  patient,
  carePlan,
}: {
  patient: Reference<Patient>
  carePlan: Reference<CarePlan>
}): UseCarePlanDemographicsResult {
  const { data: carePlanInstance } = useRead(InitialCarePlan, carePlan)

  const supportingQuestionnaireResponseReferences =
    carePlanInstance?.supportingQuestionnaireResponses

  const hasSupportingQuestionnaireResponses =
    supportingQuestionnaireResponseReferences === undefined
      ? undefined
      : supportingQuestionnaireResponseReferences.length > 0

  const supportingQuestionnaireResponsesQuery = useSearch(
    ExtendedQuestionnaireResponse,
    ExtendedQuestionnaireResponse.queryByIds(
      compact(
        supportingQuestionnaireResponseReferences?.map((r) => r.reference)
      )
    ),
    {
      query: {
        enabled: hasSupportingQuestionnaireResponses,
      },
    }
  )

  const emergencyContactQuery = useSearchOneOrCreate(
    ExtendedQuestionnaireResponse.searchQuery({
      questionnaire: questionnaires.EmergencyContactAndDecisionsQuestionnaire,
      subject: patient,
    }),
    ExtendedQuestionnaireResponse.buildFromQuestionnaire({
      questionnaire: questionnaires.EmergencyContactAndDecisionsQuestionnaire,
      subject: patient,
    }),
    {
      query: {
        enabled: hasSupportingQuestionnaireResponses === false,
      },
    }
  )

  const patientEvaluationQuery = useSearchOneOrCreate(
    ExtendedQuestionnaireResponse.searchQuery({
      questionnaire: questionnaires.PatientEvaluationQuestionnaire,
      subject: patient,
    }),
    ExtendedQuestionnaireResponse.buildFromQuestionnaire({
      questionnaire: questionnaires.PatientEvaluationQuestionnaire,
      subject: patient,
    }),
    {
      query: {
        enabled: hasSupportingQuestionnaireResponses === false,
      },
    }
  )

  const physicalFunctioningQuery = useSearchOneOrCreate(
    ExtendedQuestionnaireResponse.searchQuery({
      questionnaire: questionnaires.PatientPhysicalFunctioningQuestionnaire,
      subject: patient,
    }),
    ExtendedQuestionnaireResponse.buildFromQuestionnaire({
      questionnaire: questionnaires.PatientPhysicalFunctioningQuestionnaire,
      subject: patient,
    }),
    {
      query: {
        enabled: hasSupportingQuestionnaireResponses === false,
      },
    }
  )

  const dietQuery = useSearchOneOrCreate(
    ExtendedQuestionnaireResponse.searchQuery({
      questionnaire: questionnaires.DietQuestionnaire,
      subject: patient,
    }),
    ExtendedQuestionnaireResponse.buildFromQuestionnaire({
      questionnaire: questionnaires.DietQuestionnaire,
      subject: patient,
    }),
    {
      query: {
        enabled: hasSupportingQuestionnaireResponses === false,
      },
    }
  )

  const serviceAllocationQuery = useSearchOneOrCreate(
    ExtendedQuestionnaireResponse.searchQuery({
      questionnaire: questionnaires.ServiceAllocationQuestionnaire,
      subject: patient,
    }),
    ExtendedQuestionnaireResponse.buildFromQuestionnaire({
      questionnaire: questionnaires.ServiceAllocationQuestionnaire,
      subject: patient,
    }),
    {
      query: {
        enabled: hasSupportingQuestionnaireResponses === false,
      },
    }
  )

  const isLoading = hasSupportingQuestionnaireResponses
    ? supportingQuestionnaireResponsesQuery.isLoading
    : emergencyContactQuery.isLoading ||
      patientEvaluationQuery.isLoading ||
      physicalFunctioningQuery.isLoading ||
      dietQuery.isLoading ||
      serviceAllocationQuery.isLoading

  const error = hasSupportingQuestionnaireResponses
    ? supportingQuestionnaireResponsesQuery.error
    : (emergencyContactQuery.error ??
      patientEvaluationQuery.error ??
      physicalFunctioningQuery.error ??
      dietQuery.error ??
      serviceAllocationQuery.error)

  const demographics = useMemo(
    () =>
      mapDemographicsQuestionnaireResponses(
        hasSupportingQuestionnaireResponses
          ? (supportingQuestionnaireResponsesQuery.data ?? [])
          : [
              emergencyContactQuery.data,
              patientEvaluationQuery.data,
              physicalFunctioningQuery.data,
              dietQuery.data,
              serviceAllocationQuery.data,
            ]
      ),
    [
      emergencyContactQuery.data,
      patientEvaluationQuery.data,
      physicalFunctioningQuery.data,
      dietQuery.data,
      serviceAllocationQuery.data,
      supportingQuestionnaireResponsesQuery.data,
      hasSupportingQuestionnaireResponses,
    ]
  )

  return {
    demographics,
    isLoading,
    error,
  }
}
